/* ============================================================================
   Transport layer. The UI never calls fetch directly:
     component -> hook -> service -> request() -> fetch | mock adapter
   Swapping mock for the real API is a single env flag (VITE_USE_MOCK_API).
   ========================================================================== */

import type { ApiErrorShape } from '@core/types'
import { env } from '@core/utils/env'
import { mockRequest } from './mock/handlers'

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'

export interface RequestOptions {
  method?: HttpMethod
  /** JSON-serialisable request body. */
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 12_000

export class ApiError extends Error implements ApiErrorShape {
  readonly status: number
  readonly code: string
  readonly fieldErrors?: Record<string, string>

  constructor(shape: ApiErrorShape) {
    super(shape.message)
    this.name = 'ApiError'
    this.status = shape.status
    this.code = shape.code
    this.fieldErrors = shape.fieldErrors
  }

  /** A retry is worth offering only when the failure was not caused by the request. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/** Turns anything thrown into a message a user can act on. */
export function toErrorMessage(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'The request was cancelled.'
  }
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong. Please try again.'
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = env.apiBaseUrl || ''
  const url = new URL(`${base}${path}`, base ? undefined : (globalThis.location?.origin ?? 'http://localhost'))
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      url.searchParams.set(key, String(value))
    }
  }
  return base ? url.toString() : `${url.pathname}${url.search}`
}

export async function request<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  if (env.useMockApi) {
    return mockRequest<TResponse>(path, options)
  }

  const controller = new AbortController()
  const timeout = setTimeout(
    () => controller.abort(),
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  )
  options.signal?.addEventListener('abort', () => controller.abort(), { once: true })

  try {
    const token = await resolveAuthToken()
    const response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers: {
        Accept: 'application/json',
        ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
    })

    if (response.status === 204) {
      return undefined as TResponse
    }

    const payload: unknown = await response.json().catch(() => undefined)

    if (!response.ok) {
      throw new ApiError(normaliseErrorPayload(response.status, payload))
    }

    return payload as TResponse
  } catch (error) {
    if (error instanceof ApiError) throw error
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError({
        status: 408,
        code: 'request_timeout',
        message: 'The server took too long to answer. Check your connection and try again.',
      })
    }
    throw new ApiError({
      status: 0,
      code: 'network_unreachable',
      message: 'We could not reach the EcoNinhursag server. Check your connection and retry.',
    })
  } finally {
    clearTimeout(timeout)
  }
}

function normaliseErrorPayload(status: number, payload: unknown): ApiErrorShape {
  if (payload && typeof payload === 'object') {
    const candidate = payload as Partial<ApiErrorShape> & { detail?: string }
    return {
      status,
      code: candidate.code ?? `http_${status}`,
      message: candidate.message ?? candidate.detail ?? fallbackMessage(status),
      fieldErrors: candidate.fieldErrors,
    }
  }
  return { status, code: `http_${status}`, message: fallbackMessage(status) }
}

function fallbackMessage(status: number): string {
  if (status === 401) return 'Your session has expired. Sign in again to continue.'
  if (status === 403) return 'You do not have permission to do that.'
  if (status === 404) return 'We could not find what you were looking for.'
  if (status === 422) return 'Some of the details you entered need correcting.'
  if (status >= 500) return 'The server ran into a problem. Please try again in a moment.'
  return 'Something went wrong. Please try again.'
}

/* ------------------------------------------------------------------ token --
   Kept in one place so the auth strategy can change without touching services. */

export type AuthTokenResolver = () => string | null | Promise<string | null>

const TOKEN_KEY = 'econinhursag.sessionToken'
let inMemoryToken: string | null = null
let dynamicTokenResolver: AuthTokenResolver | null = null

export function setAuthTokenResolver(resolver: AuthTokenResolver | null): void {
  dynamicTokenResolver = resolver
}

export function getAuthToken(): string | null {
  if (inMemoryToken) return inMemoryToken
  try {
    inMemoryToken = globalThis.localStorage.getItem(TOKEN_KEY)
  } catch {
    inMemoryToken = null
  }
  return inMemoryToken
}

export async function resolveAuthToken(): Promise<string | null> {
  if (dynamicTokenResolver) {
    try {
      const resolved = await dynamicTokenResolver()
      if (resolved) return resolved
    } catch {
      /* Fallback to synchronous in-memory or storage token */
    }
  }
  return getAuthToken()
}

export function setAuthToken(token: string | null): void {
  inMemoryToken = token
  try {
    if (token) globalThis.localStorage.setItem(TOKEN_KEY, token)
    else globalThis.localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* storage can be blocked — the in-memory token still works for this session */
  }
}

